<template>
  <section class="py-12 sm:py-16 px-4" style="background-color: #ffffff;">
    <div class="max-w-7xl mx-auto">
      <h2 class="font-header text-[3rem] xl:text-[3rem] font-bold text-primary mb-8 text-center">
        Discover Your Next Great Read
      </h2>
      <div class="hex-grid-container">
        <div v-if="loading" class="text-center py-12">
          <p class="text-gray-600">Loading categories...</p>
        </div>
        <div v-else-if="categories.length === 0" class="text-center py-12">
          <p class="text-gray-600">No categories available</p>
        </div>
        <div v-else class="hex-grid"
          :style="{ width: `${gridMetrics.gridWidth}px`, height: `${gridMetrics.totalHeight}px` }">
          <a v-for="(category, index) in categories" :key="category.id" :href="`/books?category=${category.slug}`"
            :style="getHexagonPosition(index)" class="hexagon-wrapper group cursor-pointer">
            <div class="hexagon-outer">
              <div
                class="hexagon-inner flex items-center justify-center bg-white shadow-sm transition-all duration-300 group-hover:bg-[#CBDDD1] group-hover:shadow-md">
                <span
                  class="font-body text-primary font-semibold text-xs sm:text-sm xl:text-base text-center px-2 leading-[1.75] break-words transition-colors duration-300">
                  {{ category.name }}
                </span>
              </div>
            </div>
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { createAxiosInstance } from '../utils/axios.js'

const api = createAxiosInstance()

const categories = ref([])
const loading = ref(true)

// Fetch categories from API
const fetchCategories = async () => {
  try {
    loading.value = true
    const response = await api.get('/api/categories', {
      params: { limit: 100 }
    })

    // Handle both paginated and non-paginated responses
    if (response.data.pagination) {
      categories.value = response.data.categories || []
    } else {
      categories.value = Array.isArray(response.data) ? response.data : []
    }
  } catch (error) {
    console.error('Failed to fetch categories:', error)
    categories.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchCategories()
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', handleResize)
  }
})

const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 768)

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', handleResize)
  }
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', handleResize)
  }
})

const handleResize = () => {
  windowWidth.value = window.innerWidth
}

// Hexagonal grid implementation based on Red Blob Games guide:
// Reference: https://www.redblobgames.com/grids/hexagons/
//
// For pointy-top hexagons:
// - size = outer radius (distance from center to corner)
// - width = 2 * size
// - height = sqrt(3) * size
// - Therefore: height = (sqrt(3)/2) * width
//
// Spacing formulas (pointy-top orientation):
// - Horizontal distance between adjacent hex centers: horiz = 3/4 * width = 3/2 * size
// - Vertical distance: vert = height = sqrt(3) * size
//
// Using odd-r offset coordinates (shoves odd rows right):
// - Pixel conversion: x = sqrt(3) * size * (col + 0.5 * (row & 1))
//                      y = 3/2 * size * row

const getHexagonDimensions = () => {
  // Keep hexagon size constant, only adjust items per row for responsive layout
  const width = 128

  // Adjust items per row based on screen width
  if (windowWidth.value >= 1024) {
    return { width, itemsPerRow: 5 }
  } else if (windowWidth.value >= 768) {
    return { width, itemsPerRow: 4 }
  } else if (windowWidth.value >= 640) {
    return { width, itemsPerRow: 3 }
  } else {
    return { width, itemsPerRow: 2 }
  }
}

const gridMetrics = computed(() => {
  if (categories.value.length === 0) {
    return {
      size: 0,
      width: 0,
      height: 0,
      sqrt3: 0,
      gridWidth: 0,
      gridLeftOffset: 0,
      totalHeight: 0,
      itemsPerRow: 0,
      horizontalSpacing: 0,
      verticalSpacing: 0,
      oddRowOffset: 0
    }
  }

  const { width, itemsPerRow } = getHexagonDimensions()
  const sqrt3 = Math.sqrt(3)

  // size is the outer radius (half the width)
  const size = width / 2

  // height = sqrt(3) * size
  const height = sqrt3 * size

  // Horizontal spacing multiplier to prevent overlap (accounts for borders)
  // Base spacing is sqrt(3) * size, we add extra spacing
  const horizontalSpacing = 28

  // Vertical spacing between rows - reduce to match the smaller horizontal spacing within rows
  // Account for the offset nature of hexagons in adjacent rows
  const verticalSpacing = horizontalSpacing * 0.0375 * 0.64

  // Calculate grid dimensions
  const totalRows = Math.ceil(categories.value.length / itemsPerRow)
  let maxX = 0
  let minX = Infinity

  // Additional offset for odd rows (bottom row) to shift right more
  const oddRowOffset = 0.125

  for (let row = 0; row < totalRows; row++) {
    const itemsInRow = Math.min(itemsPerRow, categories.value.length - row * itemsPerRow)
    for (let col = 0; col < itemsInRow; col++) {
      // Calculate x position of hex center using odd-r formula with extra spacing
      // Add additional offset for odd rows to shift bottom row more to the right
      const rowOffset = 0.5 * (row & 1) + (row & 1) * oddRowOffset
      const x = sqrt3 * size * (col + rowOffset) + col * horizontalSpacing
      maxX = Math.max(maxX, x)
      minX = Math.min(minX, x)
    }
  }

  // Grid width is from leftmost hex left edge to rightmost hex right edge
  const gridWidth = maxX - minX + width
  const gridLeftOffset = minX - width / 2

  const totalHeight = totalRows > 0 ? (totalRows - 1) * (height + verticalSpacing) + height : height

  return { size, width, height, sqrt3, gridWidth, gridLeftOffset, totalHeight, itemsPerRow, horizontalSpacing, verticalSpacing, oddRowOffset }
})

const getHexagonPosition = (index) => {
  const { size, width, sqrt3, gridLeftOffset, itemsPerRow, horizontalSpacing, verticalSpacing, oddRowOffset } = gridMetrics.value

  // Calculate row and column in grid
  const row = Math.floor(index / itemsPerRow)
  const col = index % itemsPerRow

  // Convert odd-r offset coordinates to pixel coordinates
  // Add additional offset for odd rows to shift bottom row more to the right
  const rowOffset = 0.5 * (row & 1) + (row & 1) * oddRowOffset
  const x = sqrt3 * size * (col + rowOffset) + col * horizontalSpacing
  // Add vertical spacing between rows for consistent gap
  const y = (3 / 2) * size * row + row * verticalSpacing

  // Position relative to grid container, centered
  const left = x - gridLeftOffset - width / 2

  return {
    position: 'absolute',
    left: `${left}px`,
    top: `${y}px`
  }
}
</script>

<style scoped>
.hex-grid-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
  width: 100%;
}

.hex-grid {
  position: relative;
}

.hexagon-wrapper {
  position: absolute;
}

/* Pointy-top hexagon clip-path */
/* For pointy-top: angles are 0°, 60°, 120°, 180°, 240°, 300° */
/* Starting at top point (0°), going clockwise */
/* Height formula: height = sqrt(3) * size = sqrt(3) * width / 2 */
/* Hexagon size is constant at 128px for all screen sizes */
.hexagon-outer {
  width: 128px;
  height: calc(128px * 1.732 / 2);
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  background-color: #E8C07D;
  position: relative;
}

.hexagon-inner {
  width: calc(128px - 8px);
  height: calc((128px - 8px) * 1.732 / 2);
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
